import { Column, ColumnConfig } from '@ant-design/charts';
import { DownOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  Challenge,
  DashboardChallengeIdeaStats,
  GET_DASHBOARD_CHALLENGE_IDEA_REGIONS,
} from '@frontend/data-access';
import { Button, Card, Dropdown } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

/* eslint-disable-next-line */
export interface ChallengeDashboardRegionsProps {
  challenge: Challenge & { __typename?: string };
}

export function ChallengeDashboardRegions({
  challenge,
}: ChallengeDashboardRegionsProps) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<DashboardChallengeIdeaStats[]>([]);
  const [sortOption, setSortOption] = useState({
    key: 'HL',
    label: t('highest_to_lowest'),
  });

  const { data: rawData, loading } = useQuery<{
    dashboardChallengeIdeasByRegions: DashboardChallengeIdeaStats[];
  }>(GET_DASHBOARD_CHALLENGE_IDEA_REGIONS, {
    fetchPolicy: 'no-cache',
    variables: {
      challengeId: challenge.id,
    },
  });

  const sortOptions = useMemo(
    () => [
      { key: 'HL', label: t('highest_to_lowest') },
      { key: 'LH', label: t('lowest_to_highest') },
    ],
    [t]
  );

  const highestRegion = useMemo(() => {
    if (
      !rawData?.dashboardChallengeIdeasByRegions ||
      rawData.dashboardChallengeIdeasByRegions.length === 0
    )
      return '';
    return rawData.dashboardChallengeIdeasByRegions.reduce((max, item) =>
      item.count > max.count ? item : max
    ).name;
  }, [rawData]);

  useEffect(() => {
    if (!rawData?.dashboardChallengeIdeasByRegions) return;
    // sort
    const sortedData = [...rawData.dashboardChallengeIdeasByRegions].sort(
      (a, b) => {
        if (sortOption.key === 'HL') {
          return b.count - a.count;
        }
        return a.count - b.count;
      }
    );
    if (sortedData.length == 0) {
      setData([{ name: t('no_data'), count: 0 }]);
    } else {
      setData(sortedData);
    }
  }, [rawData, sortOption.key, t]);

  const config: ColumnConfig = {
    data: data,
    loading: loading,
    xField: 'name',
    yField: 'count',
    colorField: 'name',
    color: ({ name }: { name: string }) =>
      name === highestRegion ? '#63E063' : '#58AFFF',
    label: false,
    xAxis: {
      label: {
        autoHide: true,
        autoRotate: false,
        offset: 10,
        rotate: -45,
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
        },
      },
    },

    legend: {
      color: {
        position: 'bottom',
        autoWrap: true,
        itemMarker: 'square',
        style: {
          fontSize: 14,
          fontFamily: 'Lufga',
        },
        layout: {
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
  };

  return (
    <Card style={{ borderRadius: 20 }} data-test="challenge-dashboard-regions">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('regions_covered')}
          </div>
          <div>
            <Dropdown
              menu={{
                items: sortOptions.map((item) => ({
                  ...item,
                  onClick: () => setSortOption(item),
                })),
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button shape="round">
                {sortOption.label}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <Column key="challenge-dashboard-regions" {...config} />
      </div>
    </Card>
  );
}

export default ChallengeDashboardRegions;
